/** @jsx jsx */

/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useState } from 'react';
import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import { PressEvent } from 'react-aria-components';
import { css, jsx } from '@emotion/react';

import { Typography, VisuallyHidden } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

import MenuCollapseButton from './MenuCollapseButton';
import InAppTopbar from './UniversalTopbar/InAppTopbar/InAppTopbar';
import OutOfAppTopbar from './UniversalTopbar/OutOfAppTopbar/OutOfAppTopbar';
import TopbarLoadingState from './TopbarLoadingState';
import InAppSidebar from './UniversalSidebar/InAppSidebar/InAppSidebar';
import OutOfAppSidebar from './UniversalSidebar/OutOfAppSidebar/OutOfAppSidebar';

import { addParamsToUrl, extractParams } from '@zapier/url-utils';
import {
  useUniversalNavContext,
  shouldRenderTopbarLinksForRoute,
  useQuerySession,
} from '../hooks';

import findAccountsMenuRenderer from '../utils/findAccountsMenuRenderer';
import findNotificationsRenderer from '../utils/findNotificationsRenderer';
import useUnscrollableBody from '../hooks/useUnscrollableBody';
import {
  UNIVERSAL_NAV_ZINDEX,
  loginPath,
  signupPath,
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
  skipLinkHref as skipLinkHrefDefault,
} from '../constants';

import type { CallToAction } from '../constants';
import type { NavItem } from '../constants/links';

const Styles = labelStyles('UniversalNav', {
  root: ({
    isV3TopbarResponse,
    shouldHeaderStickToTop,
  }: {
    isV3TopbarResponse: boolean;
    shouldHeaderStickToTop: boolean;
  }) => [
    css`
      position: sticky;
      z-index: ${UNIVERSAL_NAV_ZINDEX};
      top: -${UNIVERSAL_TOPBAR_CONDENSED_HEIGHT}px;

      @media (min-width: 450px) {
        top: -${UNIVERSAL_TOPBAR_HEIGHT}px;
      }
    `,
    isV3TopbarResponse &&
      css`
        // - Styles 'My Zaps' button: https://gitlab.com/zapier/design-systems/design-system/-/blob/main/packages/universal-layout/src/universal-nav/utils/findAccountsMenuRenderer.tsx#L47-52
        & #back-to-app {
          border-radius: 40px;
          &::before {
            border-radius: 42px;
          }
          & span {
            font: var(
              --zds-body-3,
              600 14px/24px ${Typography.base.fontFamily}
            );
            color: var(--zds-gray-warm-1, #f9f7f3);
            line-height: 24px;
          }
        }

        // - Styles 'Sign up' button: https://gitlab.com/zapier/design-systems/design-system/-/blob/main/packages/universal-layout/src/universal-nav/components/UniversalTopbar/AccountActions.tsx#L84-91
        & #universalnav-signup {
          border-radius: 40px;
          &::before {
            border-radius: 42px;
          }
          & span {
            font: var(
              --zds-body-3,
              600 14px/24px ${Typography.base.fontFamily}
            );
            color: var(--zds-gray-warm-1, #f9f7f3);
            line-height: 24px;
          }
        }
      `,
    shouldHeaderStickToTop &&
      withModifier(
        'stuck-to-top',
        css`
          top: 0;
        `
      ),
  ],

  forceMenuCollapseButton: css`
    margin-left: var(--zds-space-0, 0px);
  `,

  sidebarWrapper: ({
    isFixed,
    isFullWidth,
  }: {
    isFixed: boolean;
    isFullWidth: boolean;
  }) => [
    css`
      position: absolute;
      display: flex;
      align-items: stretch;
      // Push beneath UniversalTopbar border so the border is seen.
      margin-top: 1px;
      top: 100%;
      left: 0;
      height: 100vh;
      will-change: height;
      transition: height 300ms linear;
    `,

    isFixed &&
      !isFullWidth &&
      css`
        position: fixed;
        top: 0;
        bottom: 0;
        height: auto;
      `,

    isFullWidth &&
      css`
        width: 100%;
      `,
  ],
});

export type Props = {
  /**
   * Renders notifications component.
   */
  renderNotifications?: () => ReactNode;
  /**
   * Anchor link to the main content area on the page to allow
   * screenreaders to skip to the main content.
   */
  skipLinkHref?: string;
};

type SidebarProps = {
  callToAction?: CallToAction;
  currentAccountId?: number;
  currentUrl: string;
  height: string | number;
  isFixed: boolean;
  isLoggedIn: boolean;
  isOnAppPage: boolean;
  isSidebarControlledByTopbar: boolean;
  isSidebarForcedOpen: boolean;
  links: Array<NavItem>;
  loginHref: string;
  onClick?: (e: ReactMouseEvent) => void;
  shouldRenderTopbarLinks: boolean;
  signupHref: string;
  size: 'small' | 'medium';
};

// Copies query strings from given source to destination
const copyQueryString = (source: string, destination: string) =>
  addParamsToUrl(destination, extractParams(source), true);

const Sidebar = (props: SidebarProps) => {
  const {
    shouldRenderAccessibleSidebar,
    setIsCreateMenuOpen,
    setIsMoreLinksMenuOpen,
  } = useUniversalNavContext();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isFullWidth = props.size === 'small';
  const isActuallyCollapsed =
    !isFullWidth &&
    !props.isSidebarForcedOpen &&
    !props.isSidebarControlledByTopbar &&
    isCollapsed;
  const sharedProps = {
    isCollapsed: isActuallyCollapsed,
    isFullWidth,
    isShadowed:
      isFullWidth || (!props.isSidebarForcedOpen && !isActuallyCollapsed),
    onClick: props.onClick,
    onMouseEnter: () => {
      if (window.matchMedia('(hover: hover)').matches) {
        setIsCollapsed(false);
      }
    },
    onMouseLeave: () => {
      if (window.matchMedia('(hover: hover)').matches) {
        setIsCollapsed(true);

        // Uncomment if create menu should stay open when mouse moves out of sidebar
        //if (props.isSidebarForcedOpen === false)
        setIsCreateMenuOpen(false);
        setIsMoreLinksMenuOpen(false);
      }
    },
  };

  const shouldRenderLoggedInSidebar =
    props.isLoggedIn &&
    props.currentAccountId &&
    (props.isOnAppPage || props.shouldRenderTopbarLinks);

  useUnscrollableBody({
    shouldAllowScroll:
      (!isFullWidth && !sharedProps.isShadowed) ||
      shouldRenderAccessibleSidebar,
  });

  const sidebar =
    shouldRenderLoggedInSidebar && props.currentAccountId ? (
      <InAppSidebar
        {...sharedProps}
        currentAccountId={props.currentAccountId}
        selectedHref={props.currentUrl}
      />
    ) : (
      <OutOfAppSidebar
        {...sharedProps}
        callToAction={props.callToAction}
        currentUrl={props.currentUrl}
        isActuallyCollapsed={isActuallyCollapsed}
        isOnAppPage={props.isOnAppPage}
        isLoggedIn={props.isLoggedIn}
        loginHref={props.loginHref}
        outOfAppLinks={props.links}
        signupHref={props.signupHref}
        size={props.size}
      />
    );

  const style = { height: props.height };
  return (
    <div
      css={Styles.sidebarWrapper({
        isFixed: props.isFixed,
        isFullWidth,
      })}
      style={style}
      data-zds
    >
      {sidebar}
    </div>
  );
};

/**
 * Top-level universal navigation component that renders the
 * top bar and the sidebar.
 */
export const UniversalNav = ({
  renderNotifications: renderNotificationsProp = () => null,
  skipLinkHref = skipLinkHrefDefault,
}: Props) => {
  const {
    canTopbarFitBackToAppButton,
    canForceSidebarOpen,
    currentAccountId,
    currentUrl,
    isInAppExperience,
    isLoadingTopNav,
    isOnAppPage,
    isSearchFullWidth,
    isSidebarForcedOpen,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    mode,
    onToggleSidebarVisibility,
    setIsSidebarForcedOpen,
    setIsSidebarOpenedByTopbar,
    setIsSidebarRendered,
    shouldRenderCallToAction,
    shouldRenderSearch,
    shouldRenderSidebar,
    shouldRenderAccessibleSidebar,
    shouldRenderTopbarLinks,
    topbarSize,
    topNavData,
  } = useUniversalNavContext();

  const { data: sessionData, isLoading: isSessionDataLoading } =
    useQuerySession();
  const isLoggedIn = sessionData?.isLoggedIn ?? false;

  const shouldHeaderStickToTop = shouldRenderTopbarLinksForRoute({
    currentUrl,
    mode,
  });

  useEffect(() => {
    setIsSidebarRendered(shouldRenderSidebar);
    return () => {
      setIsSidebarRendered(false);
    };
  }, [setIsSidebarRendered, shouldRenderSidebar]);

  const getLinksToRender = () => {
    if (isLoadingTopNav) {
      // Render an empty link. This will take up the space
      // in the main navigation content.
      // Avoids a shift in the logo
      return [{ text: '', href: '', variant: 'link' as const }];
    }

    return topNavData.topBarMenu;
  };

  const linksToRender = getLinksToRender();

  const loginHref = copyQueryString(currentUrl, loginPath);
  const signupHref = copyQueryString(currentUrl, signupPath);

  const renderAccountMenu = findAccountsMenuRenderer({
    canTopbarFitBackToAppButton,
    currentAccountId,
    currentUrl,
    isLoadingLoggedIn: isSessionDataLoading,
    isLoggedIn,
    loginHref,
    signupHref,
  });

  const renderNotifications = findNotificationsRenderer({
    currentUrl,
    isLoggedIn,
    renderNotifications: renderNotificationsProp,
  });

  // Use `undefined` to leverage default value, noop to prevent rendering.
  const renderSearchForm = shouldRenderSearch ? undefined : () => undefined;

  const [bottom, setBottom] = useState(Infinity);
  const navRef = useRef(null);
  useEffect(() => {
    const setNavBottom = () => {
      if (navRef.current) {
        setBottom(
          (navRef.current! as HTMLDivElement)!.getBoundingClientRect().bottom
        );
      }
    };
    setNavBottom();
    document.addEventListener('scroll', setNavBottom, { capture: true });
    window.addEventListener('resize', setNavBottom, { capture: true });
    return () => {
      document.removeEventListener('scroll', setNavBottom, { capture: true });
      window.removeEventListener('resize', setNavBottom, { capture: true });
    };
  }, []);

  const closeSidebar = () => {
    setIsSidebarOpenedByTopbar(false);
  };

  const closeSidebarOnTriggerClick = (
    e: ReactMouseEvent | MouseEvent | TouchEvent | PressEvent
  ) => {
    const target = e!.target! as HTMLElement;

    // Prevent closing the sidebar if the button is the "+ Create" menu dropdown button.
    const triggerIsCreateMenuButton = target.parentElement?.classList.contains(
      'sidebar-create-menu-button'
    );

    const triggerIsMoreButton = target.classList?.contains('MoreButton');

    const trigger = target.closest('a, button, label, [role="button"]');

    // Close the sidebar if any interactive node is clicked
    // and the sidebar is controlled by the topbar.
    if (
      isSidebarOpenedByTopbar &&
      !!trigger &&
      !triggerIsCreateMenuButton &&
      !triggerIsMoreButton
    ) {
      closeSidebar();
    }
  };

  const renderSidebar = ({ size }: { size?: 'small' | 'medium' }) => {
    const buttonLabel = isSidebarForcedOpen ? 'Close Sidebar' : 'Open Sidebar';
    const buttonIcon = isSidebarForcedOpen ? 'formX' : 'navMenu';

    const toggleSidebarVisibilityButton = (() => {
      if (onToggleSidebarVisibility) {
        return (
          <MenuCollapseButton
            ariaExpanded={isSidebarOpenedByTopbar}
            icon={isSidebarOpenedByTopbar ? 'formX' : 'navMenu'}
            label="Menu"
            onClick={onToggleSidebarVisibility}
          />
        );
      }
      if (canForceSidebarOpen) {
        return (
          <div css={Styles.forceMenuCollapseButton} data-zds>
            <MenuCollapseButton
              ariaExpanded={isSidebarForcedOpen}
              icon={buttonIcon}
              label={buttonLabel}
              onClick={() => setIsSidebarForcedOpen(!isSidebarForcedOpen)}
            />
          </div>
        );
      }
      return null;
    })();

    const sidebarLinksToRender = linksToRender || [];

    // This should only ever be reached for out-of-app pages
    const sidebar = shouldRenderSidebar && (
      <Sidebar
        callToAction={
          shouldRenderCallToAction ? topNavData.callToAction : undefined
        }
        currentAccountId={currentAccountId}
        currentUrl={currentUrl}
        height={`calc(100vh - ${Math.max(0, bottom)}px)`}
        isFixed={false} // FIXME: hack in place for Tables. (issues with Suspense ruining measurements)
        isLoggedIn={isLoggedIn}
        isOnAppPage={isOnAppPage}
        isSidebarControlledByTopbar={isSidebarControlledByTopbar}
        isSidebarForcedOpen={isSidebarForcedOpen}
        links={sidebarLinksToRender}
        loginHref={loginHref}
        onClick={closeSidebarOnTriggerClick}
        shouldRenderTopbarLinks={shouldRenderTopbarLinks}
        signupHref={signupHref}
        size={size || topbarSize}
      />
    );

    const accessibleSidebar = shouldRenderAccessibleSidebar && (
      <VisuallyHidden>
        <div data-testid="hidden-sidebar" data-zds>
          <Sidebar
            currentAccountId={currentAccountId}
            currentUrl={currentUrl}
            height={`calc(100vh - ${Math.max(0, bottom)}px)`}
            isFixed={bottom <= 0}
            isLoggedIn={isLoggedIn}
            isOnAppPage={isOnAppPage}
            isSidebarControlledByTopbar={isSidebarControlledByTopbar}
            isSidebarForcedOpen={isSidebarForcedOpen}
            links={sidebarLinksToRender || []}
            loginHref={loginHref}
            onClick={closeSidebarOnTriggerClick}
            shouldRenderTopbarLinks={shouldRenderTopbarLinks}
            signupHref={signupHref}
            size={size || topbarSize}
          />
        </div>
      </VisuallyHidden>
    );
    // Need to return the nodes only if at least one exists,
    // otherwise the empty `Fragment` will cause a slot to
    // render when it shouldn't.
    return (
      (toggleSidebarVisibilityButton || sidebar) && (
        <React.Fragment>
          {toggleSidebarVisibilityButton}
          {sidebar}
          {accessibleSidebar}
        </React.Fragment>
      )
    );
  };

  // Track mounted state to force a rerender.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <div
      css={Styles.root({
        isV3TopbarResponse: !!topNavData.linksInApp,
        shouldHeaderStickToTop,
      })}
      key={hasMounted ? 'has-mounted' : 'has-not-mounted'}
      ref={navRef}
      data-zds
    >
      {isLoadingTopNav ? (
        <TopbarLoadingState
          isSearchFullWidth={isSearchFullWidth}
          links={shouldRenderTopbarLinks ? linksToRender : []}
          onClick={closeSidebarOnTriggerClick}
          onClickOutside={closeSidebar}
          renderAccountMenu={renderAccountMenu}
          renderSearchForm={renderSearchForm}
          renderSidebar={renderSidebar}
          selectedHref={currentUrl}
          size={topbarSize}
          skipLinkHref={skipLinkHref}
        />
      ) : isInAppExperience ? (
        <InAppTopbar
          isSearchFullWidth={isSearchFullWidth}
          onClick={closeSidebarOnTriggerClick}
          onClickOutside={closeSidebar}
          renderAccountMenu={renderAccountMenu}
          renderNotifications={renderNotifications}
          renderSearchForm={renderSearchForm}
          renderSidebar={renderSidebar}
          selectedHref={currentUrl}
          size={topbarSize}
          skipLinkHref={skipLinkHref}
        />
      ) : (
        <OutOfAppTopbar
          isSearchFullWidth={isSearchFullWidth}
          links={shouldRenderTopbarLinks ? linksToRender : []}
          onClick={closeSidebarOnTriggerClick}
          onClickOutside={closeSidebar}
          renderAccountMenu={renderAccountMenu}
          renderNotifications={renderNotifications}
          renderSearchForm={renderSearchForm}
          renderSidebar={renderSidebar}
          selectedHref={currentUrl}
          size={topbarSize}
          skipLinkHref={skipLinkHref}
        />
      )}
    </div>
  );
};
